<template>
  <aside class="right-panel">
    <section class="right-panel__section">
      <h2 class="right-panel__heading">{{ $t('roleMatrix_legend') }}</h2>
      <div class="right-panel__legend">
        <div class="right-panel__legend-item">
          <b-form-checkbox :checked="true" disabled />
          <span>{{ $t('roleMatrix_permissionGranted') }}</span>
        </div>
        <div class="right-panel__legend-item">
          <b-form-checkbox :checked="false" indeterminate disabled class="right-panel__inherited" />
          <span
            >{{ $t('roleMatrix_permissionInherited') }} /
            {{ $t('roleMatrix_permissionPartial') }}</span
          >
        </div>
        <div class="right-panel__legend-item">
          <b-form-checkbox :checked="false" disabled />
          <span>{{ $t('roleMatrix_permissionNone') }}</span>
        </div>
      </div>
    </section>

    <section class="right-panel__section">
      <h2 class="right-panel__heading">{{ $t('roleMatrix_selectedCell') }}</h2>
      <h4 class="right-panel__subheading">{{ $t('roleMatrix_accessLevel') }}</h4>
      <div class="right-panel__levels">
        <button
          v-for="option in levelOptions"
          :key="option.value || 'none'"
          type="button"
          class="right-panel__level"
          :class="{ 'right-panel__level--active': effectiveLevel === option.value }"
          :disabled="!hasSelection"
          @click="$emit('set-level', option.value)"
        >
          {{ option.label }}
        </button>
      </div>
    </section>

    <section class="right-panel__section right-panel__section--grow">
      <h2 class="right-panel__heading">{{ $t('preview') }}</h2>
      <p class="right-panel__description">
        {{ $t('roleMatrix_previewDescription', { role: focusedRoleName }) }}
      </p>
      <hr class="right-panel__separator" />
      <ul v-if="previewRows.length > 0" class="right-panel__preview">
        <li
          v-for="previewRow in previewRows"
          :key="previewRow.node.id"
          class="right-panel__preview-item"
          :class="{ 'right-panel__preview-item--top-level': previewRow.depth === 0 }"
          :style="{ paddingLeft: `${previewRow.depth * previewIndentPerDepth}px` }"
        >
          {{ nodeLabel(previewRow.node) }}
          <b-badge variant="light" class="right-panel__kind">
            {{ kindLabel(previewRow.node) }}
          </b-badge>
        </li>
      </ul>
      <p v-else class="right-panel__description">{{ $t('roleMatrix_previewEmpty') }}</p>
    </section>
  </aside>
</template>

<script>
const PREVIEW_INDENT_PER_DEPTH_PX = 14;

export default {
  name: 'RightPanel',
  props: {
    levelOptions: { type: Array, required: true },
    effectiveLevel: { type: String, default: null },
    hasSelection: { type: Boolean, default: false },
    focusedRoleName: { type: String, default: '' },
    previewRows: { type: Array, required: true },
    nodeLabel: { type: Function, required: true },
    kindLabel: { type: Function, required: true },
  },
  data() {
    return { previewIndentPerDepth: PREVIEW_INDENT_PER_DEPTH_PX };
  },
};
</script>

<style lang="scss" scoped>
.right-panel {
  display: flex;
  flex-shrink: 0;
  flex-direction: column;
  width: 320px;
  border-left: 2px solid $gray-200;
  font-size: 0.875rem;
}

.right-panel__section {
  padding: 14px 16px;
  border-bottom: 1px solid $gray-200;

  &--grow {
    flex: 1;
    border-bottom: none;
  }
}

.right-panel__heading {
  margin: 0;
  color: $gray-600;
  font-size: 0.875rem;
  font-weight: 600;
  letter-spacing: 0.1em;
  text-transform: uppercase;
}

.right-panel__subheading {
  margin: 12px 0 8px;
  font-size: 0.875rem;
  font-weight: 600;
}

.right-panel__legend {
  margin-top: 12px;
  color: $gray-600;
}

.right-panel__legend-item {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
}

.right-panel__inherited {
  opacity: 0.5;
}

.right-panel__levels {
  display: flex;
  overflow: hidden;
  border: 1px solid $gray-200;
  border-radius: 6px;
}

.right-panel__level {
  flex: 1;
  padding: 4px 0;
  border: none;
  border-right: 1px solid $gray-200;
  background: none;

  &:last-child {
    border-right: none;
  }

  &--active {
    background-color: $gray-100;
    font-weight: 600;
  }
}

.right-panel__description {
  margin: 4px 0 8px;
  color: $gray-500;
}

.right-panel__separator {
  margin: 0 0 8px;
}

.right-panel__preview {
  margin: 0;
  padding: 0;
  list-style: none;
}

.right-panel__preview-item {
  padding-top: 2px;
  padding-bottom: 2px;
  color: $gray-500;

  &--top-level {
    color: inherit;
    font-weight: 600;
  }
}

.right-panel__kind {
  margin-left: 4px;
  text-transform: uppercase;
}
</style>
